import { ChangeDetectorRef, Component, Optional, ViewChild } from '@angular/core'
import { CmsSearchBoxComponent, RoutingService, WindowRef } from '@spartacus/core'
import {
  CmsComponentData,
  SearchBoxComponent,
  SearchBoxComponentService
} from '@spartacus/storefront'
import { BehaviorSubject, Observable, Subscription, filter, map, switchMap, take, tap } from 'rxjs'
import { EbProduct } from '../../../product/interfaces/eb-product.interface'
import { EbSearchResults } from './interfaces/eb-search-result.interface'

@Component({
    selector: 'eb-search-box',
    templateUrl: './eb-search-box.component.html',
    styleUrls: ['./eb-search-box.component.scss'],
    standalone: false
})
export class EbSearchBoxComponent extends SearchBoxComponent {
  @ViewChild('searchInput') searchInput
  isVisible = false
  searchTermLength: number
  isSearching$ = new BehaviorSubject(false)
  avoidClose = false
  results$: Observable<EbSearchResults> = this.config$.pipe(
    switchMap(config => this.searchBoxComponentService.getResults(config)),
    tap(() => this.setIsSearching(false))
  )
  spinnerDelay
  subscription: Subscription

  constructor(
    protected searchBoxComponentService: SearchBoxComponentService,
    @Optional()
    protected componentData: CmsComponentData<CmsSearchBoxComponent>,
    protected winRef: WindowRef,
    protected routingService: RoutingService,
    protected cd: ChangeDetectorRef
  ) {
    super(searchBoxComponentService, componentData, winRef, routingService)
  }

  open(): void {
    super.open()
    this.isVisible = true
    this.cd.detectChanges()
    this.searchInput.nativeElement.focus()

    this.avoidClose = false
  }

  get isSearching(): Observable<boolean> {
    return this.isSearching$
  }

  close(event: UIEvent, force = false): void {
    if (!this.avoidClose) {
      this.blurSearchBox(event)
      this.isVisible = false
      this.searchBoxComponentService.clearResults()
    }
  }

  clear(): void {
    this.avoidClose = true;
    this.clearInputField();
    this.searchBoxComponentService.clearResults();
    this.searchTermLength = 0;
    this.chosenWord = '';
  }

  getProductUrl(result: EbSearchResults, product?: EbProduct) {
    if (result.keywordRedirectUrl) {
      const [urlWithoutParams] = result.keywordRedirectUrl.split(/\?/)
      return urlWithoutParams
    } else {
      return {
        cxRoute: 'ebParentProduct',
        params: product
      }
    }
  }

  getQueryParams(url: string): { [key: string]: string } {
    const [, paramString] = url.split(/\?/)
    if (paramString) {
      const params = new URLSearchParams(paramString)
      const searchTerm = params.get('searchTerm')
      return { searchTerm }
    }
    return {}
  }

  search(query: string): void {
    this.setIsSearching(true)
    this.searchTermLength = query.length
    super.search(query)
  }

  launchSearchResult(event: UIEvent, query: string): void {
    if (!query || query.trim().length === 0) {
      return
    }
    this.setIsSearching(true)
    this.results$
      .pipe(
        filter(result => !!result.products),
        map(result => {
          if (result.products.length === 1 && result.keywordRedirectUrl) {
            this.clear()
            this.routingService.goByUrl(result.keywordRedirectUrl)
          } else {
            this.searchBoxComponentService.launchSearchPage(query)
          }
          this.close(event)
        }),
        take(1)
      )
      .subscribe()
    this.search(query)
  }

  setIsSearching(isSearching) {
    // this delays the appearance of the loading spinner in the search-box a bit so the spinner doesn´t flicker with every keystroke
    if (isSearching) {
      if (this.spinnerDelay) {
        clearTimeout(this.spinnerDelay)
        this.spinnerDelay = null
      }
      this.spinnerDelay = setTimeout(() => {
        this.isSearching$.next(isSearching)
      }, 100)
    } else {
      this.isSearching$.next(isSearching)
      clearTimeout(this.spinnerDelay)
      this.spinnerDelay = null
    }
  }

  clearInputField() {
    const inputElement = this.searchInput?.nativeElement;
    if (inputElement) {
      inputElement.value = '';
      inputElement.focus();
    }
  }

  ngOnInit(): void {
    this.subscription = this.routingService
      .getRouterState()
      .pipe(filter((data) => !data.nextState))
      .subscribe(() => {
        this.clear()
      });
  }
}
